import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import { fetchAllCommercantProduitsForPicker } from '../utils/linkedProduitPickerCatalog';
import { mergeProductRow, parseAssignRawValue } from '../utils/restaurantPrepConfigModel';

/**
 * Données postes + produits sans rechargement complet (évite sauts de scroll).
 */
export function useRestaurantPrepConfig(commercantId) {
  const [stations, setStations] = useState([]);
  const [products, setProducts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [stationBusy, setStationBusy] = useState(false);
  const [productSaveState, setProductSaveState] = useState(null);
  const mountedRef = useRef(true);
  const productsRef = useRef(products);
  const productSaveChainRef = useRef(Promise.resolve());

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadAll = useCallback(
    async ({ soft = false } = {}) => {
      if (!commercantId) return;
      if (!soft) setInitialLoading(true);
      setLoadError(null);
      try {
        const [stRes, prodList] = await Promise.all([
          apiService.listKitchenPrepStations(commercantId, { includeInactive: true }),
          fetchAllCommercantProduitsForPicker(
            apiService.getProduitsByCommercant.bind(apiService),
            commercantId
          ),
        ]);
        if (!mountedRef.current) return;
        setStations(stRes?.stations || []);
        setProducts(prodList);
      } catch (e) {
        if (mountedRef.current) setLoadError(e.message || 'Chargement impossible');
      } finally {
        if (mountedRef.current && !soft) setInitialLoading(false);
      }
    },
    [commercantId]
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const upsertStation = useCallback(
    async (payload) => {
      if (!commercantId) return null;
      setStationBusy(true);
      try {
        const res = await apiService.upsertKitchenPrepStation(payload, commercantId);
        const station = res?.station || res;
        setStations((prev) => {
          const idx = prev.findIndex((s) => s.code === station.code);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], ...station };
            return next;
          }
          return [...prev, station].sort(
            (a, b) =>
              (a.sortOrder || 0) - (b.sortOrder || 0) ||
              String(a.label).localeCompare(String(b.label), 'fr')
          );
        });
        return station;
      } finally {
        if (mountedRef.current) setStationBusy(false);
      }
    },
    [commercantId]
  );

  const runSerializedProductSave = useCallback((task) => {
    const run = productSaveChainRef.current.then(task, task);
    productSaveChainRef.current = run.catch(() => {});
    return run;
  }, []);

  const assignOneProduct = useCallback(
    (produitId, rawValue) =>
      runSerializedProductSave(async () => {
        const previous = productsRef.current.find((p) => p.id === produitId);
        if (!previous) return { ok: false };
        const { preparationStationId, requiresPreparation } = parseAssignRawValue(rawValue);
        const optimistic = { ...previous, preparationStationId, requiresPreparation };
        setProducts((list) => list.map((p) => (p.id === produitId ? optimistic : p)));
        setProductSaveState({ kind: 'single', produitId, status: 'saving' });
        try {
          const res = await apiService.assignProductKitchenPrepStation(
            produitId,
            { preparationStationId, requiresPreparation },
            commercantId
          );
          const updated = res?.produit || res;
          setProducts((list) =>
            list.map((p) => (p.id === produitId ? mergeProductRow(p, updated) : p))
          );
          setProductSaveState({ kind: 'single', produitId, status: 'saved' });
          return { ok: true, produit: updated };
        } catch (e) {
          setProducts((list) => list.map((p) => (p.id === produitId ? previous : p)));
          setProductSaveState({
            kind: 'single',
            produitId,
            status: 'error',
            message: e.message,
          });
          return { ok: false, error: e };
        }
      }),
    [commercantId, runSerializedProductSave]
  );

  const assignManyProducts = useCallback(
    (produitIds, rawValue) =>
      runSerializedProductSave(async () => {
        if (!produitIds.length) return { ok: false, error: new Error('Aucune sélection') };
        const { preparationStationId, requiresPreparation } = parseAssignRawValue(rawValue);
        const idSet = new Set(produitIds);
        const snapshots = new Map(
          productsRef.current.filter((p) => idSet.has(p.id)).map((p) => [p.id, p])
        );
        setProducts((list) =>
          list.map((p) =>
            idSet.has(p.id) ? { ...p, preparationStationId, requiresPreparation } : p
          )
        );
        setProductSaveState({ kind: 'batch', count: produitIds.length, status: 'saving' });
        try {
          const res = await apiService.assignProductKitchenPrepStationsBatch(
            {
              assignments: produitIds.map((produitId) => ({
                produitId,
                preparationStationId,
                requiresPreparation,
              })),
            },
            commercantId
          );
          const results = res?.results || [];
          const byId = new Map(
            results.filter((r) => r.ok && r.produit).map((r) => [r.produitId, r.produit])
          );
          const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.produitId));
          setProducts((list) =>
            list.map((p) => {
              if (failedIds.has(p.id) && snapshots.has(p.id)) return snapshots.get(p.id);
              if (byId.has(p.id)) return mergeProductRow(p, byId.get(p.id));
              return p;
            })
          );
          const failed = res?.failed ?? 0;
          setProductSaveState({
            kind: 'batch',
            count: produitIds.length,
            status: failed > 0 ? 'partial' : 'saved',
            succeeded: res?.succeeded,
            failed,
          });
          return { ok: failed === 0, outcome: res };
        } catch (e) {
          setProducts((list) =>
            list.map((p) =>
              idSet.has(p.id) && snapshots.has(p.id) ? snapshots.get(p.id) : p
            )
          );
          setProductSaveState({ kind: 'batch', status: 'error', message: e.message });
          return { ok: false, error: e };
        }
      }),
    [commercantId, runSerializedProductSave]
  );

  return {
    stations,
    products,
    initialLoading,
    loadError,
    stationBusy,
    productSaveState,
    reload: loadAll,
    upsertStation,
    assignOneProduct,
    assignManyProducts,
  };
}
